
import React from "react";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { BaseService, TournamentService, ITournamentEditionRegistered, SanitizedResponse, ITournamentEditionMatch, TournamentMatchStatus, ITournamentEditionRegisteredParticipant, ITournamentEditionScoringRecord, ITournamentEditionScoring } from "../../../../services";
import { Checkbox, Classname, Column, DateHelper, DOMHelper, ScrollPanel, Table } from "@rivtn/noseur";
import { Schemes, Utils } from "../../../../utils";
import { Elements } from "../../../shared/Elements";
import MatchCard, { showTournamentPass } from "./shared/MatchCard";

export interface YourTourHomeProps {
    registeredTournamentParticipant: ITournamentEditionRegisteredParticipant;
}

function YourTourHome(props: YourTourHomeProps) {

    const today = new Date();
    const navigate = ReactRouterDom.useNavigate();
    const { registeredTournamentParticipant } = props;
    const tournamentService = TournamentService.getInstance();
    const labels = i18nManager.Labels.tournaments.your_tournament.home;
    const [matches, setMatches] = React.useState<ITournamentEditionMatch[]>();
    const [searchParams, _] = ReactRouterDom.useSearchParams(window.location.search);
    const [tournamentScoring, setTournamentScoring] = React.useState<ITournamentEditionScoring>();
    const tournamentEdition = registeredTournamentParticipant.tournament_edition_registered.tournament_edition;
    const [tournamentScoreRecords, setTournamentScoreRecords] = React.useState<ITournamentEditionScoringRecord[]>([]);
    const tournamentDay = DateHelper.getDaysBetweenDates(new Date(tournamentEdition.dates.tournament_start_date ?? ""), today);
    const [useBgBanner, setUseBgBanner] = React.useState<boolean>(registeredTournamentParticipant.tournament_edition_registered.use_banner_background ?? false);

    React.useEffect(fetchInformation, []);

    return (<div className="your-tour-home" style={useBgBanner ? { backgroundImage: `url('${tournamentEdition.banner_url}')` } : {}}>
        <div className="header">
            <div className="dcount">
                <i className="fa fa-calendar" style={{ marginRight: 10 }} />
                <span>{labels.tour_day} {tournamentDay}</span>
            </div>
            <Checkbox scheme={Schemes.OMNEHOST} checked={useBgBanner} label={labels.use_banner_bg} onChecked={setUseBgBanner} />
        </div>
        <div className="cards">
            <div className="card">
                <i className="fa fa-list icon" />
                <div className="snapper">
                    {Elements.buildSnapper(() => {
                        showTournamentPass(tournamentEdition.name, i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.designs_headers.matches_left, registeredTournamentParticipant.tournament_edition_registered.soft_cache?.matches_left_pass_id);
                    })}
                </div>
                <span className="lined-limit l1">{labels.you_have}</span>
                <div className="lined-limit l2">
                    <span>{registeredTournamentParticipant.tournament_edition_registered.own_matches_detail.matches_left}</span>
                    <span>{labels.matches_left}</span>
                </div>
                <span className="lined-limit l3" dangerouslySetInnerHTML={{ __html: labels.you_have_matches_in_total.replace("${count}", `${registeredTournamentParticipant.tournament_edition_registered.own_matches_detail.matches_total}`) }} />
                <span className="lined-limit l4" dangerouslySetInnerHTML={{ __html: labels.total_of_in_tour.replace("${count}", `${registeredTournamentParticipant.tournament_edition_registered.tournament_edition.meta.total_matches ?? 0}`) }} />
            </div>
            <div className="card">
                <i className="fa fa-ribbon icon" />
                <div className="snapper">
                    {Elements.buildSnapper(() => {
                        showTournamentPass(tournamentEdition.name, i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.designs_headers.matches_won, registeredTournamentParticipant.tournament_edition_registered.soft_cache?.matches_left_pass_id);
                    })}
                </div>
                <span className="lined-limit l1">{labels.you_have_won}</span>
                <div className="lined-limit l2">
                    <span style={{ color: "#0FA883" }}>{registeredTournamentParticipant.tournament_edition_registered.own_matches_detail.matches_won}</span>
                    <span>{labels.matches}</span>
                </div>
                <span className="lined-limit l3" dangerouslySetInnerHTML={{ __html: labels.and_lost_match.replace("${count}", `${registeredTournamentParticipant.tournament_edition_registered.own_matches_detail.matches_lost}`) }} />
                <span className="lined-limit l4" dangerouslySetInnerHTML={{ __html: labels.may_the_universe_be_with_you }} />
            </div>
            <div className="card">
                <i className="fa fa-trophy icon" />
                <div className="snapper">
                    {Elements.buildSnapper(() => {
                        showTournamentPass(tournamentEdition.name, i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.designs_headers.tour_ranking, registeredTournamentParticipant.tournament_edition_registered.soft_cache?.matches_left_pass_id);
                    })}
                </div>
                <span className="lined-limit l1">{labels.you_currently_rank_top}</span>
                <div className="lined-limit l2">
                    <span>{registeredTournamentParticipant.tournament_edition_registered.own_standings.position}</span>
                </div>
                <span className="lined-limit l3" dangerouslySetInnerHTML={{ __html: labels.above_other_parts.replace("${count}", `${Math.max(0, registeredTournamentParticipant.tournament_edition_registered.own_standings.count_below)}`) }} />
                <span className="lined-limit l4" dangerouslySetInnerHTML={{ __html: labels.and_below_others.replace("${count}", `${Math.max(0, registeredTournamentParticipant.tournament_edition_registered.own_standings.count_above)}`) }} />
            </div>
        </div>
        <div className="container">
            <div className="uo-matches">
                <span className="t">{labels.upcoming_oncoming_matches}</span>
                <ScrollPanel className="matches">
                    {!matches && (Elements.LOADING_STATE)}
                    {matches && !matches?.length && (Elements.buildEmptyListView("fa fa-list", (<>
                        <span>{labels.empty_parts.you_have_no}</span>{" "}
                        <ReactRouterDom.NavLink to={{ pathname: `/tournaments/${registeredTournamentParticipant.tournament_edition_registered.external_id}`, search: "tab=MATCHES_AND_REPORTING" }}>{labels.empty_parts.matches_and_reportings}</ReactRouterDom.NavLink>{" "}
                        <span>{labels.empty_parts.for_past_matches}</span>
                    </>), { marginBottom: 50 }))}
                    {(!!(matches && matches.length)) && (matches.map((match, i) => (<MatchCard key={DOMHelper.uniqueElementId("omatch")}
                        tournamentEditionRegisteredParticipantId={registeredTournamentParticipant.external_id} match={match} tournamentName={tournamentEdition.name} onAction={(match, action = "") => {
                            navigate({ pathname: `/tournaments/${registeredTournamentParticipant.tournament_edition_registered.external_id}`, search: `tab=MATCHES_AND_REPORTING&mid=${match.external_id}&a=${action}` });
                        }} />)))}
                </ScrollPanel>
            </div>
            <div className="standings">
                <div className="header">
                    <span className="t">{labels.standings}</span>
                    <div className="snapper">
                        {Elements.buildSnapper(() => {
                            showTournamentPass(tournamentEdition.name, i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.designs_headers.tour_ranking, registeredTournamentParticipant.tournament_edition_registered.soft_cache?.matches_left_pass_id);
                        })}
                    </div>
                </div>
                {!tournamentScoreRecords.length ? Elements.LOADING_STATE : (<Table stripedRows scheme={Schemes.OMNEHOST} data={tournamentScoreRecords} totalRecords={tournamentScoreRecords.length} dataRefreshKeys={["scores"]}
                    style={{ flex: 1, marginTop: 20 }} rowsPerPage={5000} loadingState={Elements.LOADING_STATE}
                    valuedRowProps={(d) => {
                        return {
                            className: Classname.build("your-standings-row", { "active": registeredTournamentParticipant.external_id === d?.tournament_edition_registered_participant?.external_id })
                        };
                    }}>
                    <Column className="table-c" header={labels.place} dataKey="position" />
                    <Column className="table-c" header={labels.participant} dataKey="tournament_edition_registered_participant" template={(v) => {
                        const name = Utils.getResourceName(v.resource);
                        const imageUrl = Utils.getResourceImage(v.resource);

                        return (<div style={{ display: "flex", alignItems: "center" }}>
                            <img alt={name} src={imageUrl} style={{ borderRadius: "100%", width: 30, height: 30, marginRight: 10 }} />
                            <span>{name}</span>
                        </div>);
                    }} />
                    {/* <>{Object.keys(tournamentScoring?.scoring ?? {}).map((score) => (<Column key={score} className="table-c" header={score} dataKey="scores" template={(v) => {
                        return (`${v[score] ?? 0} ${labels.scoring_system_map[leaderboard.scores[score].toLowerCase()]}`);
                    }} />))}</> */}
                    {(Object.keys(tournamentScoring?.scoring ?? {}).length > 0) ? (<React.Fragment key={DOMHelper.uniqueElementId("tslbo")}>
                        {Object.values(tournamentScoring?.scoring ?? {}).map((scoreSetting) => (<Column key={scoreSetting.id} className="table-c" header={scoreSetting.name} template={(v) => {
                            return (<span key={scoreSetting.id} style={{ marginTop: 5, width: "max-content" }}>{v.scores?.[scoreSetting.id]} {i18nManager.Labels.tournaments.your_tournament.home.scoring_system_map[scoreSetting.score_system.toLowerCase()]}</span>);
                        }} />))}
                    </React.Fragment>) : <Column />}
                </Table>)}
            </div>
        </div>
    </div>);

    function fetchInformation() {
        tournamentService.queryTournamentEditionScorings(tournamentEdition.external_id!, {
            type: "TOURNAMENT_SCORE",
        }).then(({ content }: SanitizedResponse<ITournamentEditionScoring>) => {
            if (content?.length) {
                setTournamentScoring(content[0]);
            }
        }).catch(BaseService.reportError);
        tournamentService.queryTournamentEditionScoringRecords(tournamentEdition.external_id!, {
            sort: "position,ASC",
        }).then(({ content }: SanitizedResponse<ITournamentEditionScoringRecord>) => {
            setTournamentScoreRecords(content);
        }).catch(BaseService.reportError);
        tournamentService.queryRegisteredTournamentMatches(registeredTournamentParticipant.tournament_edition_registered.tournament_edition.external_id, {
            sort: "start_date,ASC",
            status__in: [TournamentMatchStatus.ONGOING, TournamentMatchStatus.UPCOMING],
            "tournament_edition_registered_participant_ids": registeredTournamentParticipant.external_id,
        }).then(({ content }: SanitizedResponse<ITournamentEditionMatch>) => {
            setMatches(content!);
        }).catch(BaseService.reportError);
        setTournamentScoreRecords([]);
    }

}

export default YourTourHome;
